/**
 * QaFeedbackService — QA 피드백을 채널 Tasks 보드의 태스크 카드로 제출
 *
 * 제출 경로 (Q1: prod 단일 + 실패 시 dev 폴백):
 *  1) 운영 브리지 — dev callable `qaFeedbackBridgeRelay` → prod ingest → 운영 채널에
 *     오너 author 로 카드 생성 (functions/src/qa-bridge-api.ts).
 *  2) 폴백 — 브리지 미구성/실패 시 기존 방식: `qa-comm.json` 의 feedbackChannelId
 *     채널 Tasks 보드에 클라이언트 직접 insert (dev Supabase).
 *     RLS: scope='channel' AND type='tasks' 는 채널 멤버 누구나 insert 가능 →
 *     QA 계정이 해당 채널 멤버여야 한다(운영 준비 항목). 미멤버면 명확한 에러로 안내.
 *
 * 카드: 제목 `[QA-F###] 제목`, 본문 = 설명 + 환경 요약, 첨부 = 주석 PNG + diagnostics.txt.
 * 이 모듈은 IS_QA_ELIGIBLE 게이트 뒤에서만 호출되어 운영 빌드에서 DCE 로 제거된다.
 */
import { supabase } from './SupabaseClient';
import {
  fetchTasksBoard, createPost, uploadPostAttachment, notifyNewPost,
} from './BoardService';
import { My } from './My';
import { getFunc } from './FireClient';
import { getQaCommConfig } from '../config/qaFeedback';
import type { PostAuthor, PostAttachment, TaskMetadata } from '../models/Board';
import type { FeedbackEnvironment, FeedbackDiagnostics } from '../models/Feedback';

/** 브리지 성공 시 카드가 생성되는 운영 앱 origin (링크는 새 탭으로 연다) */
const PROD_APP_ORIGIN = 'https://app.nextpod.jp';

export type QaSubmitFailReason = 'no-board' | 'not-member' | 'unknown';

export class QaFeedbackError extends Error {
  reason: QaSubmitFailReason;
  constructor(reason: QaSubmitFailReason, message: string) {
    super(message);
    this.name = 'QaFeedbackError';
    this.reason = reason;
  }
}

export interface QaSubmitResult {
  postId: string;
  channelId: string;
  title: string;   // [QA-F###] 접두 포함 최종 제목
  /** 카드가 속한 채널 링크 — target='prod' 면 절대 URL(새 탭), 'dev' 면 앱 내 라우트 */
  link: string;
  /** 카드가 실제로 생성된 환경 — 'prod'(브리지) | 'dev'(직접 insert 폴백) */
  target: 'prod' | 'dev';
}

/** 해당 보드에서 다음 QA 피드백 번호 계산 — 기존 `[QA-F` 카드 count + 1 */
async function nextQaNumber(boardId: string): Promise<number> {
  if (!supabase) return 1;
  const { count } = await supabase
    .from('posts')
    .select('*', { count: 'exact', head: true })
    .eq('board_id', boardId)
    .ilike('title', '[QA-F%');
  return (count ?? 0) + 1;
}

/** 환경 요약 문자열 (카드 본문 하단). 전체 스냅샷은 diagnostics.json 첨부로. */
function buildEnvSummary(env: FeedbackEnvironment, diag: FeedbackDiagnostics): string {
  const errCount = (diag.jsErrors?.length ?? 0) + (diag.networkErrors?.length ?? 0);
  const lines = [
    '──────────',
    `버전: ${env.appDescribe ?? env.appVersion} (${env.appCommit ?? '-'})`,
    `URL: ${env.currentUrl}`,
    `워크스페이스: ${env.activeWorkspaceName ?? env.activeWorkspaceId ?? '-'}`,
    `플랫폼: ${env.platform} · ${env.browserInfo ?? '-'} · ${env.osInfo ?? '-'}`,
    `화면: ${env.viewportWidth}×${env.viewportHeight} @${env.devicePixelRatio}x`,
    `최근 에러: JS ${diag.jsErrors?.length ?? 0} · 네트워크 ${diag.networkErrors?.length ?? 0} (합 ${errCount})`,
  ];
  return lines.join('\n');
}

function isRlsError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  const msg = (e?.message ?? '').toLowerCase();
  return e?.code === '42501' || msg.includes('row-level security') || msg.includes('row level security');
}

export interface QaSubmitParams {
  annotatedBlob: Blob;
  title: string;
  description: string;
  environment: FeedbackEnvironment;
  diagnostics: FeedbackDiagnostics;
}

/** Blob → base64 (data: 접두 제거) — relay 전송용 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * QA 피드백 제출 — 운영 브리지(relay) 우선, 실패 시 dev 채널 직접 insert 폴백.
 * 두 경로 모두 실패하면 QaFeedbackError(reason) 로 원인을 구분해 던진다.
 */
export async function submitQaFeedback(params: QaSubmitParams): Promise<QaSubmitResult> {
  try {
    return await submitViaBridge(params);
  } catch (err) {
    // 브리지 미구성(failed-precondition)·네트워크 오류 등 — 기존 dev 직접 등록으로 폴백
    console.warn('[QaFeedback] 운영 브리지 실패 → dev 채널 직접 등록 폴백:', err);
  }
  return submitDirectToDevChannel(params);
}

/** 경로 1 — dev callable relay → prod ingest → 운영 채널 카드 생성 */
async function submitViaBridge(params: QaSubmitParams): Promise<QaSubmitResult> {
  const { annotatedBlob, title, description, environment, diagnostics } = params;
  const annotatedPngBase64 = await blobToBase64(annotatedBlob);
  const fn = getFunc('qaFeedbackBridgeRelay');
  const { data } = await fn({
    title: title.trim(),
    description: description.trim(),
    environment,
    diagnostics,
    annotatedPngBase64,
  });
  const result = data as { postId: string; title: string; channelId: string; link: string };
  return {
    postId: result.postId,
    channelId: result.channelId,
    title: result.title,
    link: `${PROD_APP_ORIGIN}${result.link}`,
    target: 'prod',
  };
}

/** 경로 2 (폴백) — qa-comm.json 채널 Tasks 보드에 클라이언트 직접 insert */
async function submitDirectToDevChannel(params: QaSubmitParams): Promise<QaSubmitResult> {
  const { annotatedBlob, title, description, environment, diagnostics } = params;
  const { feedbackChannelId } = getQaCommConfig();

  // 1. 채널 Tasks 보드 조회
  const board = await fetchTasksBoard(feedbackChannelId);
  if (!board) {
    throw new QaFeedbackError('no-board', 'QA 피드백 채널에 Tasks 보드가 없습니다.');
  }

  // 2. QA 번호 → 최종 제목
  const num = await nextQaNumber(board.id);
  const seq = String(num).padStart(3, '0');
  const finalTitle = `[QA-F${seq}] ${title.trim()}`;

  const author: PostAuthor = {
    uid: My.user.id,
    name: My.user.name,
    photoUrl: My.user.photoUrl || null,
  };

  // 3. 첨부 업로드 — 주석 PNG + 진단 스냅샷 JSON
  const attachments: PostAttachment[] = [];
  try {
    const pngFile = new File([annotatedBlob], `qa-${seq}-annotated.png`, { type: 'image/png' });
    const png = await uploadPostAttachment(pngFile, My.user.id, board.id);
    attachments.push({ ...png, source: 'local' });

    // board-attachments 버킷 allowed_mime_types 에 application/json 이 없음 → text/plain 으로 업로드
    // (내용은 그대로 JSON 포맷 텍스트. 파일명은 .diagnostics.txt)
    const diagJson = JSON.stringify({ environment, diagnostics }, null, 2);
    const jsonFile = new File([diagJson], `qa-${seq}-diagnostics.txt`, { type: 'text/plain' });
    const doc = await uploadPostAttachment(jsonFile, My.user.id, board.id);
    attachments.push({ ...doc, source: 'local' });
  } catch (err) {
    if (isRlsError(err)) {
      throw new QaFeedbackError('not-member', 'QA 피드백 채널의 멤버가 아니어서 첨부를 올릴 수 없습니다.');
    }
    throw err;
  }

  // 4. 본문 = 설명 + 환경 요약
  const plainText = `${description.trim()}\n\n${buildEnvSummary(environment, diagnostics)}`;

  // 5. 태스크 카드 생성
  const now = new Date().toISOString();
  const metadata: TaskMetadata & Record<string, any> = {
    status: 'requested',
    assignee: null,
    creator: author,
    due_date: null,
    source_type: 'manual',
    source_thread_id: null,
    source_scope: null,
    source_room_id: feedbackChannelId,
    watchers: [My.user.id],
    last_activity_at: now,
    // QA 전용 부가 메타 (카드 필터/식별용)
    qa_feedback: true,
    qa_seq: num,
    qa_env: {
      appDescribe: environment.appDescribe ?? environment.appVersion,
      appCommit: environment.appCommit ?? null,
      currentUrl: environment.currentUrl,
      platform: environment.platform,
    },
  };

  let post;
  try {
    post = await createPost({
      board_id: board.id,
      workspace_id: board.workspace_id,
      title: finalTitle,
      plain_text: plainText,
      author,
      tags: [],
      metadata: metadata as unknown as Record<string, any>,
      attachments,
    });
  } catch (err) {
    if (isRlsError(err)) {
      throw new QaFeedbackError('not-member', 'QA 피드백 채널의 멤버가 아니어서 카드를 등록할 수 없습니다.');
    }
    throw err;
  }

  // 6. 알림 (표준 게시판 경로 — 실패해도 제출 자체엔 영향 없음)
  notifyNewPost(board.workspace_id, board.id, board.name, finalTitle, post.id);

  return {
    postId: post.id,
    channelId: feedbackChannelId,
    title: finalTitle,
    link: `/channel/${feedbackChannelId}`,
    target: 'dev',
  };
}
